import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Menu, MenuItem, Tooltip, TooltipProps, styled, tooltipClasses } from "@mui/material";
import React, { MouseEvent, ReactElement, useCallback, useEffect } from 'react';
import { IStatusListDataObject, IStatusListItemData } from './StatusList';
import { AppSettings } from '@/AppSettings';
import { useSessionStorage } from 'react-use';
import { grey } from '@mui/material/colors';

const StatusListSortTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        minWidth: theme.spacing(10),
        fontSize: '1rem',
        fontWeight: 'normal',
        padding: theme.spacing(2),
        backgroundColor: grey[700]
    },
}));

const StyledMenuItem = styled(MenuItem)(({ theme}) => ({
    gap: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
}));

const StyledDivider = styled(Divider)(({ theme}) => ({
    margin: `0 !important`
}));

export const DefaultSortableFields = {
    priority: {
        name: 'Priority',
        sort: (a, b) => (a.calculated.verificationPriority ?? 0) > (b.calculated.verificationPriority ?? 0) ? 1 : -1
    } as IStatusListSortable,
    participants: {
        name: 'Participant Count',
        sort: (a, b) => (a.status?.participantCount ?? 0) > (b.status?.participantCount ?? 0) ? 1 : -1
    } as IStatusListSortable,
    crfCompletion: {
        name: 'CRF Completion',
        sort: (a, b) => (a.calculated.formsComplete ?? 0) > (b.calculated.formsComplete ?? 0) ? 1 : -1
    } as IStatusListSortable,
    openQueries: {
        name: 'Open Queries',
        sort: (a, b) => (a.calculated.queriesOpen ?? 0) > (b.calculated.queriesOpen ?? 0) ? 1 : -1
    } as IStatusListSortable,
    reviewCompletion: {
        name: 'Review Completion',
        sort: (a, b) => (a.calculated.reviewComplete ?? 0) > (b.calculated.reviewComplete ?? 0) ? 1 : -1
    } as IStatusListSortable,
    verificationCompletion: {
        name: 'Verification Completion',
        sort: (a, b) => (a.calculated.verificationComplete ?? 0) > (b.calculated.verificationComplete ?? 0) ? 1 : -1
    } as IStatusListSortable,
    status: {
        name: 'Status',
        sort: (a, b) => (a.status ?? 1) > (b.status ?? 1) ? 1 : -1
    } as IStatusListSortable
}

export interface IStatusListSortable<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    sort: (itemA: IStatusListItemData<TData, TAdditional>, itemB: IStatusListItemData<TData, TAdditional>) => -1 | 0 | 1;
    name: string;
}

export interface IStatusListSortProps<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    sortKey: string;
    sortableFields?: IStatusListSortable<TData, TAdditional>[],
    onChange: (sort: {field: IStatusListSortable<TData, TAdditional>; direction: 'asc' | 'desc'}) => void;
}

const StatusListSort = <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
    sortableFields,
    sortKey,
    onChange
}: IStatusListSortProps<TData, TAdditional>): ReactElement<any, any> | null => {
    const [sortName, setSortName] = useSessionStorage<string | undefined>(`${sortKey}-sort`, undefined);
    const [sortDirection, setSortDirection] = useSessionStorage<'asc' | 'desc' | undefined>(`${sortKey}-sort-direction`, 'asc')
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);


    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const onSortSelected = useCallback((field: IStatusListSortable<TData>, direction: 'asc' | 'desc') => {
        setSortName(field.name);
        setSortDirection(direction);
        handleClose();

        onChange({ field, direction });
    }, [setSortDirection, setSortName, handleClose])

    useEffect(() => {
        if (!sortableFields || sortableFields.length == 0) {
            return;
        }

        let field = sortableFields.find(x => x.name === sortName) ?? sortableFields[0];

        //if (sortName !== field.name) {
            onSortSelected(field, sortDirection ?? 'asc');
        //}
    }, [sortableFields, onSortSelected, sortName, sortDirection]);

    const onFieldSelected = useCallback((field: IStatusListSortable<TData>) => {
        setSortName(field.name);
        handleClose();

        if (sortDirection) {
            onChange({ field, direction: sortDirection });
        }
    }, [setSortName, handleClose, sortableFields, sortDirection])

    const onDirectionSelected = useCallback((direction: 'asc' | 'desc') => {
        setSortDirection(direction);
        handleClose();

        let field = sortableFields?.find(x => x.name === sortName) ?? sortableFields?.[0];

        if (field) {
            onChange({ field, direction });
        }
    }, [setSortDirection, handleClose, sortableFields, sortName])

    if (!sortableFields || sortableFields.length == 0) {
        return null;
    }

    return (
        <>
            <StatusListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Sort Field</strong>
                        <Box>
                            {sortName}
                        </Box>
                    </>
                }
            >
                <Button
                    id="btn-field"
                    color="inherit"
                    aria-label="institution selection"
                    aria-controls="menu-field"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth style={{fontSize: '1rem'}} />}
                >
                    {sortName}
                </Button>
            </StatusListSortTooltip>
            <Menu
                id="menu-field"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-field'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                {
                    sortableFields?.map((x, i, a) => {
                        return (
                            [
                                <StyledMenuItem key={x.name} onClick={() => onFieldSelected(x)}>{x.name}</StyledMenuItem>,
                                i < (a.length - 1) ? <StyledDivider key={`${x.name}-divider`} /> : null
                            ]
                        );
                    })
                }
            </Menu>
            <StatusListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Sort Direction</strong>
                        {
                            sortDirection === 'desc' ? 
                                <Box>
                                    Descending <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth />
                                </Box> :
                                <Box> 
                                    Ascending <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth />
                                </Box>
                        }
                    </>
                }
            >
                <Button
                    id="btn-direction"
                    color="inherit"
                    aria-label="institution selection"
                    aria-controls="menu-direction"
                    aria-haspopup="true"
                    sx={{ml: 2}}
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth style={{fontSize: '1rem'}} />}
                >
                    {sortDirection === 'desc' ? <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth /> : <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth />}
                </Button>
            </StatusListSortTooltip>
            <Menu
                id="menu-direction"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-direction'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                <StyledMenuItem onClick={() => onDirectionSelected('asc')}>Ascending <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth /></StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem onClick={() => onDirectionSelected('desc')}>Descending  <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth /></StyledMenuItem>
            </Menu>
        </>
    );
}

export default StatusListSort;